import { getApiClient } from '@apps/web/lib/api_client';
import {
    ApiError,
    type components,
    type Mode,
} from '@libraries/typescript/api_client';

const AGENT_RUN_PATH = '/organizations/{organization_id}/runs/{run_id}';

export type AgentRunSummary = Readonly<components['schemas']['AgentRunRead']>;

export type AgentChatDestination =
    | {
          readonly kind: 'run';
          readonly runId: string;
          readonly summary: AgentRunSummary;
      }
    | { readonly kind: 'panel'; readonly chatId: string };

export type AgentChatNavigationSearch = {
    readonly organizationId?: string;
    readonly mode?: Mode;
};

export type AgentChatDestinationRequest = {
    readonly chatId: string;
    readonly organizationId: string;
    readonly mode: Mode;
};

export type AgentChatDestinationResolution =
    | {
          readonly kind: 'resolved';
          readonly destination: AgentChatDestination;
      }
    | { readonly kind: 'stale' };

export type AgentChatResolver = (
    request: AgentChatDestinationRequest,
) => Promise<AgentChatDestinationResolution>;

export async function locateAgentRunContext(request: {
    readonly chatId: string;
}): Promise<{ organizationId: string; mode: Mode } | null> {
    try {
        const response = await getApiClient().get({
            url: '/users/me/resource-context/{resource_type}/{resource_id}',
            pathParams: {
                resource_type: 'agent_run',
                resource_id: request.chatId,
            },
            reason: 'v3-agent-run-context',
        });
        return {
            organizationId: response.organization_id,
            mode: response.mode,
        };
    } catch (error: unknown) {
        if (error instanceof ApiError && error.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * Customer runs use the conversation trace API; the run trace API only
 * accepts internal, orchestrator, and subagent runs.
 */
export function getAgentTraceDestination(
    runId: string,
    conversationId: string | null,
):
    | {
          readonly to: '/admin/agent-traces/$conversationId';
          readonly params: { readonly conversationId: string };
      }
    | {
          readonly to: '/admin/agent-traces/run/$runId';
          readonly params: { readonly runId: string };
      } {
    if (conversationId !== null) {
        return {
            to: '/admin/agent-traces/$conversationId',
            params: { conversationId },
        };
    }
    return { to: '/admin/agent-traces/run/$runId', params: { runId } };
}

/** Shareable pathname for the same trace `getAgentTraceDestination` opens. */
export function getAgentTracePath(
    runId: string,
    conversationId: string | null,
): string {
    if (conversationId !== null) {
        return `/admin/agent-traces/${encodeURIComponent(conversationId)}`;
    }
    return `/admin/agent-traces/run/${encodeURIComponent(runId)}`;
}

/** Chooses the product surface from the frozen run discriminator alone. */
export function chooseAgentChatDestination(
    summary: AgentRunSummary,
    chatId: string,
): AgentChatDestination {
    if (summary.is_agent_v3 === true) {
        return { kind: 'run', runId: summary.id, summary };
    }
    return { kind: 'panel', chatId };
}

/**
 * Builds the canonical V3 route search from the two supported scope keys.
 * New search keys are intentionally discarded until they are reviewed here.
 */
export function normalizeAgentChatSearch(
    search: Readonly<Record<string, unknown>>,
): AgentChatNavigationSearch {
    const organizationId = search['organizationId'];
    const mode = search['mode'];
    const validOrganizationId =
        typeof organizationId === 'string' && organizationId.length > 0;
    const validMode = mode === 'live' || mode === 'sandbox';

    if (validOrganizationId && validMode) {
        return { organizationId, mode };
    }
    if (validOrganizationId) {
        return { organizationId };
    }
    if (validMode) {
        return { mode };
    }
    return {};
}

async function fetchAgentRunSummary(
    request: AgentChatDestinationRequest,
): Promise<AgentRunSummary> {
    const summary = await getApiClient().get({
        url: AGENT_RUN_PATH,
        pathParams: {
            organization_id: request.organizationId,
            run_id: request.chatId,
        },
        mode: request.mode,
        reason: 'v3-agent-run-summary',
    });
    if (summary.id !== request.chatId) {
        throw new Error('Agent run summary did not match the requested run.');
    }
    return summary;
}

/**
 * Resolves candidate summaries while making every older in-flight request
 * stale as soon as a newer request starts. Errors remain unchanged for the
 * current request and are suppressed only after that request becomes stale.
 */
export function createAgentChatDestinationResolver(): AgentChatResolver {
    let latestRequestNumber = 0;

    return async function resolveAgentChatDestination(
        request: AgentChatDestinationRequest,
    ): Promise<AgentChatDestinationResolution> {
        latestRequestNumber += 1;
        const requestNumber = latestRequestNumber;

        try {
            const summary = await fetchAgentRunSummary(request);
            if (requestNumber !== latestRequestNumber) {
                return { kind: 'stale' };
            }
            return {
                kind: 'resolved',
                destination: chooseAgentChatDestination(
                    summary,
                    request.chatId,
                ),
            };
        } catch (error: unknown) {
            if (requestNumber !== latestRequestNumber) {
                return { kind: 'stale' };
            }
            throw error;
        }
    };
}
